import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import type { Columna } from '../ui/Tabla';
import estilos from './FichaRegistro.module.css';

interface FichaRegistroProps<T> {
  /** Registro a mostrar; `null` mantiene la ficha cerrada. */
  item: T | null;
  titulo: string;
  /** Las mismas columnas de la tabla: se reaprovecha cómo pintan cada dato. */
  columnas: readonly Columna<T>[];
  /** Columnas que no aportan nada en la ficha, como la de botones. */
  omitir?: readonly string[];
  onCerrar: () => void;
  /** Botones propios, delante de «Cerrar». */
  acciones?: React.ReactNode;
}

const OMITIR_POR_DEFECTO = ['acciones'];

/**
 * Ficha de un registro.
 *
 * Una tabla ancha obliga a desplazarse en horizontal para leer una sola fila, y
 * en el móvil directamente esconde columnas. Aquí se ve todo de una vez, sin
 * riesgo de tocar nada: es la alternativa a abrir el formulario de edición
 * sólo para consultar un dato, que es como se borra algo sin querer.
 *
 * Los datos se reparten en tarjetas por filas, no en una lista de arriba a
 * abajo: con doce campos una lista obliga a desplazar el diálogo entero para
 * ver el último, y deja media anchura sin usar.
 *
 * Reutiliza las columnas de la tabla en lugar de declarar los campos otra vez.
 * Así una columna nueva aparece sola en la ficha, y ninguna de las dos se queda
 * atrás respecto de la otra.
 */
export function FichaRegistro<T>({
  item,
  titulo,
  columnas,
  omitir = OMITIR_POR_DEFECTO,
  onCerrar,
  acciones,
}: FichaRegistroProps<T>) {
  if (!item) return null;

  const visibles = columnas.filter((c) => !omitir.includes(c.clave));

  return (
    <Modal
      abierto
      onCerrar={onCerrar}
      titulo={titulo}
      // Con muchos datos el diálogo se ensancha para caber en tres columnas.
      ancho={visibles.length > 6}
      descripcion="Sólo consulta. Para cambiar algo, usa el botón de editar."
      pie={
        <>
          {acciones}
          <Button variante="secundario" onClick={onCerrar}>
            Cerrar
          </Button>
        </>
      }
    >
      <dl className={estilos.lista}>
        {visibles.map((columna) => (
          <div
            key={columna.clave}
            className={[estilos.fila, columna.fichaAncha ? estilos.filaAncha : '']
              .filter(Boolean)
              .join(' ')}
          >
            <dt className={estilos.etiqueta}>{columna.encabezado}</dt>
            <dd className={estilos.dato}>{columna.render(item)}</dd>
          </div>
        ))}
      </dl>
    </Modal>
  );
}
